import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getBoardUsage } from '../context/accountUtils';
import {
  generateSmartBoard,
  reviseSmartBoard,
  commitSmartBoard,
} from '../services/smartBoardApi';

const NEEDS = [
  'Everyday needs',
  'Food & Drink',
  'Feelings & emotions',
  'School/learning',
  'Activities',
  'Social conversation',
  'Pain/health',
  'Personal care',
  'Places/travel',
];


const BACK_IMAGE_URL = 'https://static.arasaac.org/pictograms/38249/38249_300.png';
const MORE_IMAGE_URL = 'https://static.arasaac.org/pictograms/38252/38252_300.png';

function getTileImageUrl(tile) {
  const label = String(tile?.label || '').trim().toLowerCase();
  const isNavigation = Boolean(tile?.linked_board_key);

  if (isNavigation && label === 'back') return BACK_IMAGE_URL;
  if (isNavigation && label === 'more') return MORE_IMAGE_URL;
  return tile?.image_url || '';
}

const STEPS = [
  'Who is it for?',
  'Communication',
  'Complexity',
  'Personalise',
  'Preview',
];

const blankProfile = {
  board_for: '',
  communication_needs: [],
  complexity: 'standard',
  favourite_foods: '',
  favourite_activities: '',
  important_people: '',
  important_places: '',
  dislikes: '',
  custom_words: '',
  ideal_board: '',
};

async function updateSmartSetupStatus(status) {
  const { data } = await api.post('https://jmew.xyz/aac/api/smart_setup_status.php', { status });
  return data;
}

function TilePreview({ tile, onOpen }) {
  const linked = Boolean(tile.linked_board_key);
  const imageUrl = getTileImageUrl(tile);
  return (
    <button
      type="button"
      className="smart-tile"
      style={{ backgroundColor: tile.color || '#eeeeee' }}
      onClick={() => linked && onOpen(tile.linked_board_key)}
      disabled={!linked}
      title={linked ? `Open ${tile.linked_board_key}` : tile.label}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="smart-tile-image" />
      ) : (
        <div className="smart-tile-placeholder" aria-hidden="true">
          {linked ? '↗' : '●'}
        </div>
      )}
      <span>{tile.label}</span>
    </button>
  );
}

function BoardPreview({ draft }) {
  const [activeKey, setActiveKey] = useState(draft.main_board_key);
  const board = draft.boards.find((b) => b.key === activeKey) || draft.boards[0];

  const gridStyle = {
    gridTemplateColumns: `repeat(${board.grid_config.columns}, minmax(0, 1fr))`,
  };

  return (
    <div className="smart-preview">
      <div className="smart-preview-header">
        <div>
          <strong>{board.name}</strong>
          <small>{draft.summary}</small>
        </div>
        {activeKey !== draft.main_board_key && (
          <button type="button" className="smart-link" onClick={() => setActiveKey(draft.main_board_key)}>
            Main board
          </button>
        )}
      </div>

      <div className="smart-board-grid" style={gridStyle}>
        {board.tiles.map((tile) => (
          <TilePreview
            key={`${board.key}-${tile.position}`}
            tile={tile}
            onOpen={setActiveKey}
          />
        ))}
      </div>

      {draft.boards.length > 1 && (
        <div className="smart-board-tabs">
          {draft.boards.map((b) => (
            <button
              key={b.key}
              type="button"
              className={activeKey === b.key ? 'active' : ''}
              onClick={() => setActiveKey(b.key)}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SmartBoardSetup({
  onboarding = false,
  onSkip,
  onCreated,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(blankProfile);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editWords, setEditWords] = useState('');
  const [boardUsage, setBoardUsage] = useState(null);
  const [checkingBoardLimit, setCheckingBoardLimit] = useState(true);

  const refreshBoardUsage = async () => {
    const usage = await getBoardUsage();

    if (usage) {
      setBoardUsage(usage);
      return usage;
    }

    return null;
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setCheckingBoardLimit(true);
      try {
        const usage = await getBoardUsage();
        if (cancelled) return;

        if (usage) {
          setBoardUsage(usage);

          if (usage.isLimitReached) {
            setError(
              `Board limit reached. You have used ${usage.count} of ${usage.limit} boards. ` +
              'Delete a board or upgrade your plan before creating another.'
            );
          }
        }
      } finally {
        if (!cancelled) setCheckingBoardLimit(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const packageBoardCount = result?.draft?.boards?.length || 0;
  const packageFits =
    !boardUsage ||
    (packageBoardCount > 0 && packageBoardCount <= boardUsage.remaining);

  const canNext = useMemo(() => {
    if (checkingBoardLimit) return false;
    if (boardUsage?.isLimitReached) return false;
    if (step === 0) return true; // every question can be skipped
    if (step === 1) return true;
    return true;
  }, [step, checkingBoardLimit, boardUsage]);

  const setField = (key, value) =>
    setProfile((prev) => ({ ...prev, [key]: value }));

  const toggleNeed = (value) =>
    setProfile((prev) => ({
      ...prev,
      communication_needs: prev.communication_needs.includes(value)
        ? prev.communication_needs.filter((x) => x !== value)
        : [...prev.communication_needs, value],
    }));

  const createPreview = async () => {
    setBusy(true);
    setError('');
    try {
      const usage = await refreshBoardUsage();

      if (usage?.isLimitReached) {
        setError(
          `Board limit reached. You have used ${usage.count} of ${usage.limit} boards. ` +
          'Delete a board or upgrade your plan before creating another.'
        );
        return;
      }

      const data = await generateSmartBoard(profile);
      setResult(data);

      const latestUsage = data?.board_usage || usage;
      if (latestUsage) setBoardUsage(latestUsage);

      const required = data?.package_board_count ?? data?.draft?.boards?.length ?? 0;
      if (latestUsage && required > latestUsage.remaining) {
        setError(
          `This setup creates ${required} boards, but you only have ${latestUsage.remaining} ` +
          `board slot${latestUsage.remaining === 1 ? '' : 's'} remaining ` +
          `(${latestUsage.count}/${latestUsage.limit} used). ` +
          'Choose a simpler setup, delete some boards, or upgrade your plan.'
        );
      }

      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.detail || 'Could not build your board.');
    } finally {
      setBusy(false);
    }
  };

  const revise = async (changes) => {
    if (!result?.draft_token) return;
    setBusy(true);
    setError('');
    try {
      const data = await reviseSmartBoard(result.draft_token, changes);
      setResult(data);

      const latestUsage = data?.board_usage || boardUsage;
      if (latestUsage) setBoardUsage(latestUsage);

      const required = data?.package_board_count ?? data?.draft?.boards?.length ?? 0;
      if (latestUsage && required > latestUsage.remaining) {
        setError(
          `This setup creates ${required} boards, but you only have ${latestUsage.remaining} remaining. ` +
          'Choose a simpler setup, delete some boards, or upgrade your plan.'
        );
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not update the preview.');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!result?.draft_token) return;
    setBusy(true);
    setError('');
    try {
      const usage = await refreshBoardUsage();
      const required = result?.draft?.boards?.length || 0;

      if (usage?.isLimitReached || (usage && required > usage.remaining)) {
        setError(
          usage?.isLimitReached
            ? `Board limit reached. You have used ${usage.count} of ${usage.limit} boards.`
            : `This setup needs ${required} board slots, but you only have ${usage.remaining} remaining. ` +
              'Choose a simpler setup, delete some boards, or upgrade your plan.'
        );
        return;
      }

      const data = await commitSmartBoard(result.draft_token);

      // Only first-login onboarding should change the persistent setup state.
      // Using the same component later from "Add Board" must not affect it.
      if (onboarding) {
        await updateSmartSetupStatus('completed');
      }

      if (onCreated) {
        onCreated(data);
      } else {
        navigate(`/board/${data.main_board_id}`, { replace: onboarding });
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not create your boards.');
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setError('');

    try {
      // A skip during first-login onboarding is remembered across every device.
      // If this component is opened later as a normal board creator, do not
      // alter the user's onboarding state.
      if (onboarding) {
        setBusy(true);
        await updateSmartSetupStatus('skipped');
      }

    if (onSkip) onSkip();
      else navigate('/boards', { replace: onboarding });
    } catch (e) {
      setError(
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        'Could not save your setup preference. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="smart-setup-shell">
      <div className="smart-setup-card">
        <div className="smart-top-row">
          <div>
            <span className="smart-eyebrow">{onboarding ? 'Welcome to iBoard AAC' : 'Smart Board Creator'}</span>
            <h1>{step === 4 ? 'Your personalised board' : STEPS[step]}</h1>
          </div>
          <button type="button" className="smart-skip" onClick={skip} disabled={busy}>
            Skip
          </button>
        </div>

        <div className="smart-progress" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
          {STEPS.map((_, i) => (
            <span key={i} className={i <= step ? 'done' : ''} />
          ))}
        </div>

        {error && <div className="smart-error">{error}</div>}

        {boardUsage && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 12px',
              borderRadius: '10px',
              background: boardUsage.isLimitReached ? '#fff4f4' : '#f3f8ff',
              border: `1px solid ${boardUsage.isLimitReached ? '#fecaca' : '#cfe4ff'}`,
              color: boardUsage.isLimitReached ? '#991b1b' : '#374151',
              fontSize: '14px'
            }}
          >
            Boards used: <strong>{boardUsage.count}/{boardUsage.limit}</strong>
            {step === 4 && packageBoardCount > 0 && (
              <>
                {' · '}This setup will add <strong>{packageBoardCount}</strong> board
                {packageBoardCount === 1 ? '' : 's'}.
              </>
            )}
          </div>
        )}

        {step === 0 && (
          <section className="smart-step">
            <p>Who are you setting this communication board up for?</p>
            <div className="smart-choice-grid">
              {['Myself', 'My child', 'Someone I support', 'A student', 'A client'].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={profile.board_for === v ? 'selected' : ''}
                  onClick={() => setField('board_for', v)}
                >
                  {v}
                </button>
              ))}
            </div>
            <label>
              Or enter a name / description
              <input
                value={profile.board_for}
                onChange={(e) => setField('board_for', e.target.value)}
                placeholder="e.g. Jamie"
              />
            </label>
          </section>
        )}

        {step === 1 && (
          <section className="smart-step">
            <p>What would you like help communicating? Select as many as you like.</p>
            <div className="smart-choice-grid">
              {NEEDS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={profile.communication_needs.includes(v) ? 'selected' : ''}
                  onClick={() => toggleNeed(v)}
                >
                  {v}
                </button>
              ))}
            </div>
            <label>
              Anything else about the board?
              <textarea
                rows={3}
                value={profile.ideal_board}
                onChange={(e) => setField('ideal_board', e.target.value)}
                placeholder="e.g. An everyday home board with clear ways to ask for help and say when something is wrong."
              />
            </label>
          </section>
        )}

        {step === 2 && (
          <section className="smart-step">
            <p>How detailed should the boards be?</p>
            <div className="smart-complexity">
              {[
                ['simple', 'Simple', 'Fewer, larger choices and up to 3 boards.'],
                ['standard', 'Standard', 'A balanced starting system with linked categories.'],
                ['detailed', 'Detailed', 'More vocabulary and more linked boards.'],
              ].map(([value, title, desc]) => (
                <button
                  key={value}
                  type="button"
                  className={profile.complexity === value ? 'selected' : ''}
                  onClick={() => setField('complexity', value)}
                >
                  <strong>{title}</strong>
                  <span>{desc}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="smart-step">
            <p>These are optional, but they make the starter board feel much more personal.</p>
            <div className="smart-form-grid">
              <label>
                Favourite foods & drinks
                <input
                  value={profile.favourite_foods}
                  onChange={(e) => setField('favourite_foods', e.target.value)}
                  placeholder="Pizza, apple juice, chicken nuggets"
                />
              </label>
              <label>
                Favourite activities
                <input
                  value={profile.favourite_activities}
                  onChange={(e) => setField('favourite_activities', e.target.value)}
                  placeholder="Football, Lego, swimming"
                />
              </label>
              <label>
                Important people
                <input
                  value={profile.important_people}
                  onChange={(e) => setField('important_people', e.target.value)}
                  placeholder="Mum, Dad, Grandad, teacher"
                />
              </label>
              <label>
                Important places
                <input
                  value={profile.important_places}
                  onChange={(e) => setField('important_places', e.target.value)}
                  placeholder="Home, school, Grandma's house"
                />
              </label>
              <label>
                Things they dislike
                <input
                  value={profile.dislikes}
                  onChange={(e) => setField('dislikes', e.target.value)}
                  placeholder="Loud noises, haircuts"
                />
              </label>
              <label>
                Other words or phrases to include
                <input
                  value={profile.custom_words}
                  onChange={(e) => setField('custom_words', e.target.value)}
                  placeholder="Bluey, Minecraft, I need a break"
                />
              </label>
            </div>
          </section>
        )}

        {step === 4 && result?.draft && (
          <section className="smart-step">
            <BoardPreview draft={result.draft} />

            <div className="smart-revise">
              <h3>Make a quick change</h3>
              <div className="smart-inline-actions">
                <button type="button" onClick={() => revise({ complexity: 'simple' })}>Make simpler</button>
                <button type="button" onClick={() => revise({ complexity: 'standard' })}>Standard</button>
                <button type="button" onClick={() => revise({ complexity: 'detailed' })}>More detailed</button>
              </div>
              <div className="smart-add-row">
                <input
                  value={editWords}
                  onChange={(e) => setEditWords(e.target.value)}
                  placeholder="Add words: trampoline, Bluey, pizza"
                />
                <button
                  type="button"
                  disabled={!editWords.trim() || busy}
                  onClick={async () => {
                    await revise({ add_words: editWords });
                    setEditWords('');
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </section>
        )}

        <div className="smart-footer">
          {step > 0 && step < 4 && (
            <button type="button" className="secondary" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          )}

          <div className="smart-footer-spacer" />

          {step < 3 && (
            <button
              type="button"
              className="primary"
              disabled={!canNext || busy}
              onClick={() => setStep((s) => s + 1)}
            >
              Continue
            </button>
          )}

          {step === 3 && (
            <button
              type="button"
              className="primary"
              disabled={busy || checkingBoardLimit || boardUsage?.isLimitReached}
              onClick={createPreview}
            >
              {busy ? 'Building…' : checkingBoardLimit ? 'Checking board limit…' : 'Build my board'}
            </button>
          )}

          {step === 4 && (
            <>
              <button type="button" className="secondary" disabled={busy} onClick={() => setStep(0)}>
                Start again
              </button>
              <button
                type="button"
                className="primary"
                disabled={
                  busy ||
                  checkingBoardLimit ||
                  boardUsage?.isLimitReached ||
                  !packageFits
                }
                onClick={commit}
              >
                {busy
                  ? 'Creating…'
                  : !packageFits
                    ? 'Not enough board slots'
                    : 'Create these boards'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
